import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/skeleton';
import axios from '../utils/Axios';
import saveErrorMessage from '../utils/saveErrorMessage';
import { useAuthContext } from '../auth/useAuthContext';
import {
  HolidayDialog,
  accountZone,
  accountMidnight,
  dayCount,
  formatDay,
  holidayDaysOf,
} from '../sections/@dashboard/holidays';

const TABLE_HEAD = ['Holiday Name', 'Start date', 'End date', 'Days', 'Repeat', ''];

// Rows with readable days first, in start day order. Rows with no readable days last.
function compareRows(a, b) {
  if (!a.days || !b.days) return Number(!a.days) - Number(!b.days);
  if (a.days.startDay !== b.days.startDay) return a.days.startDay < b.days.startDay ? -1 : 1;
  return 0;
}

export default function Holiday() {
  const { user } = useAuthContext();
  const zone = accountZone(user);

  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState({ open: false, holiday: null });
  const [menu, setMenu] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const showNotice = useCallback((message, severity = 'success') => {
    setNotice({ message, severity });
    setTimeout(() => setNotice(null), 6000);
  }, []);

  const loadHolidays = useCallback(async () => {
    try {
      const response = await axios.get('/holidays');
      setHolidays(response.data.categories || []);
    } catch (error) {
      console.error(error);
      showNotice('Failed to load holidays. Please refresh the page.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showNotice]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const rows = useMemo(
    () => holidays.map((holiday) => ({ holiday, days: holidayDaysOf(holiday, zone) })).sort(compareRows),
    [holidays, zone]
  );

  // A page past the end can remain after a delete. Show the last page instead.
  const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const pageRows = rows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const openDialog = (holiday) => {
    setMenu(null);
    setDialog({ open: true, holiday });
  };

  const closeDialog = () => setDialog((current) => ({ ...current, open: false }));

  // Sends the day strings, and the old `startDate` and `endDate` for an API that does not
  // read the day strings yet. PUT answers with the record from before the update, so the
  // list reloads after every save.
  const saveHoliday = async ({ name, startDay, endDay, isRecurring }) => {
    const payload = {
      name,
      startDay,
      endDay,
      startDate: accountMidnight(startDay, zone),
      endDate: accountMidnight(endDay, zone),
      isRecurring,
    };
    const editing = dialog.holiday;
    try {
      if (editing) {
        await axios.put(`/holidays/${editing._id}`, payload);
      } else {
        await axios.post('/holidays', payload);
      }
    } catch (error) {
      throw new Error(saveErrorMessage(error, 'Error saving holiday. Please try again.'));
    }
    closeDialog();
    showNotice(editing ? 'Holiday updated successfully!' : 'Holiday created successfully!');
    await loadHolidays();
  };

  const deleteHoliday = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`/holidays/${toDelete._id}`);
      setHolidays((current) => current.filter((holiday) => holiday._id !== toDelete._id));
      showNotice('Holiday deleted successfully!');
    } catch (error) {
      showNotice(saveErrorMessage(error, 'Error deleting holiday. Please try again.'), 'error');
    } finally {
      setIsDeleting(false);
      setToDelete(null);
    }
  };

  // The dialog resets its form when this object changes, so it changes only with the row.
  const dialogHoliday = useMemo(() => {
    const holiday = dialog.holiday;
    if (!holiday) return null;
    const days = holidayDaysOf(holiday, zone);
    return {
      name: holiday.name,
      startDay: days?.startDay,
      endDay: days?.endDay,
      isRecurring: holiday.isRecurring,
    };
  }, [dialog.holiday, zone]);

  return (
    <>
      <Helmet>
        <title>Holiday | {localStorage.getItem('companyName') ?? 'FlowBookings'}</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Holiday
          </Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => openDialog(null)}
          >
            Add New
          </Button>
        </Stack>

        {notice && (
          <Alert
            severity={notice.severity}
            color={notice.severity === 'success' ? 'info' : notice.severity}
            sx={{ mb: 2 }}
            onClose={() => setNotice(null)}
          >
            {notice.message}
          </Alert>
        )}

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 100 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {TABLE_HEAD.map((label, index) => (
                      <TableCell key={index}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {isLoading &&
                  [...Array(5)].map((_, index) => <TableSkeleton key={index} shouldShowImage />)}
                {!isLoading && (
                  <TableBody>
                    {pageRows.map(({ holiday, days }) => {
                      const count = days && dayCount(days.startDay, days.endDay);
                      return (
                        <TableRow hover key={holiday._id}>
                          <TableCell component="th" scope="row" style={{ width: 300 }}>
                            <Typography variant="subtitle2" noWrap>
                              {holiday.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{days ? formatDay(days.startDay) : '-'}</TableCell>
                          <TableCell>{days ? formatDay(days.endDay) : '-'}</TableCell>
                          <TableCell>{days ? `${count} ${count === 1 ? 'Day' : 'Days'}` : '-'}</TableCell>
                          <TableCell>
                            <Label color={holiday.isRecurring ? 'success' : 'warning'}>
                              {holiday.isRecurring ? 'Yearly' : 'One Off'}
                            </Label>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="large"
                              color="inherit"
                              onClick={(event) => setMenu({ anchor: event.currentTarget, holiday })}
                            >
                              <Iconify icon="eva:more-vertical-fill" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!rows.length && (
                      <TableRow>
                        <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                          <Typography variant="h6">No Holidays</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setPage(0);
              setRowsPerPage(parseInt(event.target.value, 10));
            }}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(menu)}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': { px: 1, typography: 'body2', borderRadius: 0.75 },
          },
        }}
      >
        <MenuItem onClick={() => openDialog(menu.holiday)}>
          <Iconify icon="eva:edit-fill" sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            setToDelete(menu.holiday);
            setMenu(null);
          }}
        >
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>

      <HolidayDialog
        open={dialog.open}
        holiday={dialogHoliday}
        onClose={closeDialog}
        onSave={saveHoliday}
      />

      <Dialog open={Boolean(toDelete)} onClose={() => !isDeleting && setToDelete(null)}>
        <DialogTitle>Delete holiday</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Delete the holiday "{toDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" disabled={isDeleting} onClick={() => setToDelete(null)}>
            Cancel
          </Button>
          <LoadingButton color="error" variant="contained" loading={isDeleting} onClick={deleteHoliday}>
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
